'use client';

import { Check, MoreVertical, PlusSquare, Share, Smartphone, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/utils';
import ResponsiveDialog from '@/components/responsive-dialog';
import useTranslation from '@/utils/i18n/client/useTranslation';

const DISMISS_KEY = 'pwa-install-dismissed';

export type Step = { icon: React.ReactNode; title: string; body: string };

type TFunc = (key: string) => string;

// the iOS "Add to Home Screen" steps, shared by the in-app prompt and the
// standalone install help page so the wording stays in one place
export function getIosSteps(t: TFunc): Step[] {
  return [
    {
      icon: <Share className="size-7" strokeWidth={1.75} />,
      title: t('1. Tap the Share button'),
      body: t(
        "It's the square with an arrow pointing up, at the bottom of your Safari screen (top on iPad)."
      )
    },
    {
      icon: <PlusSquare className="size-7" strokeWidth={1.75} />,
      title: t('2. Tap "Add to Home Screen"'),
      body: t(
        'Scroll down the share menu until you see "Add to Home Screen" and tap it.'
      )
    },
    {
      icon: <Check className="size-7" strokeWidth={1.75} />,
      title: t('3. Tap "Add"'),
      body: t(
        'Tap "Add" in the top-right corner. The Redi Rooms icon will appear on your home screen - open it from there from now on.'
      )
    }
  ];
}

// the Android "Add to Home screen" steps, shared by the in-app prompt and the
// standalone install help page
export function getAndroidSteps(t: TFunc): Step[] {
  return [
    {
      icon: <MoreVertical className="size-7" strokeWidth={1.75} />,
      title: t('1. Open the browser menu'),
      body: t('Tap the menu icon (three dots) in your browser toolbar.')
    },
    {
      icon: <PlusSquare className="size-7" strokeWidth={1.75} />,
      title: t('2. Tap "Add to Home screen"'),
      body: t('Choose "Add to Home screen" or "Install app" from the menu.')
    },
    {
      icon: <Check className="size-7" strokeWidth={1.75} />,
      title: t('3. Confirm'),
      body: t(
        'Tap "Add" or "Install". The Redi Rooms icon will appear on your home screen - open it from there from now on.'
      )
    }
  ];
}

// a small swipeable "how to install" guide - one clear step per card with
// dots and Back/Next, so tenants can follow along instead of reading a wall
// of instructions
export function StepSlider({
  steps,
  doneLabel,
  onDone
}: {
  steps: Step[];
  doneLabel: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const step = steps[index];
  const isLast = index === steps.length - 1;

  return (
    <div className="space-y-5">
      <div className="flex min-h-[190px] flex-col items-center justify-center gap-3 rounded-xl border bg-muted/50 px-5 py-6 text-center">
        <div className="flex size-14 items-center justify-center rounded-full bg-primary/10 text-primary">
          {step.icon}
        </div>
        <p className="text-base font-semibold">{step.title}</p>
        <p className="text-sm text-muted-foreground">{step.body}</p>
      </div>

      <div className="flex items-center justify-center gap-2">
        {steps.map((_, i) => (
          <span
            key={i}
            className={cn(
              'size-2 rounded-full transition-colors',
              i === index ? 'bg-primary' : 'bg-muted-foreground/30'
            )}
          />
        ))}
      </div>

      <div className="flex gap-2">
        {index > 0 ? (
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => setIndex((i) => i - 1)}
          >
            {t('Back')}
          </Button>
        ) : null}
        {isLast ? (
          <Button className="flex-1" onClick={onDone}>
            {doneLabel}
          </Button>
        ) : (
          <Button className="flex-1" onClick={() => setIndex((i) => i + 1)}>
            {t('Next')}
          </Button>
        )}
      </div>
    </div>
  );
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function isIos() {
  if (typeof navigator === 'undefined') {
    return false;
  }
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export function isAndroid() {
  if (typeof navigator === 'undefined') {
    return false;
  }
  return /android/i.test(navigator.userAgent);
}

export function isStandalone() {
  if (typeof window === 'undefined') {
    return false;
  }
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari specific flag
    (window.navigator as unknown as { standalone?: boolean }).standalone ===
      true
  );
}

export default function InstallPrompt({
  forceShow = false
}: {
  forceShow?: boolean;
}) {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showIosDialog, setShowIosDialog] = useState(false);
  const [showAndroidDialog, setShowAndroidDialog] = useState(false);

  useEffect(() => {
    if (isStandalone() || typeof window === 'undefined') {
      return;
    }
    // this is the tenant's one chance right after onboarding to enable
    // push notifications, so ignore any earlier dismissal on this device
    if (!forceShow && window.localStorage.getItem(DISMISS_KEY) === 'true') {
      return;
    }

    if (isIos() || isAndroid()) {
      // Android's beforeinstallprompt may or may not fire (not all browsers
      // support it, and Chrome only offers it once engagement heuristics are
      // met), so show the banner unconditionally and fall back to manual
      // instructions if no native prompt shows up.
      setShowBanner(true);
    }

    if (isIos()) {
      return;
    }

    const handler = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [forceShow]);

  const dismiss = () => {
    setShowBanner(false);
    window.localStorage.setItem(DISMISS_KEY, 'true');
  };

  const closeIosDialog = (value: boolean) => {
    setShowIosDialog(value);
    if (!value) {
      dismiss();
    }
  };

  const closeAndroidDialog = (value: boolean) => {
    setShowAndroidDialog(value);
    if (!value) {
      dismiss();
    }
  };

  const handleInstallClick = async () => {
    if (isIos()) {
      setShowIosDialog(true);
      return;
    }
    if (!deferredPrompt) {
      if (isAndroid()) {
        setShowAndroidDialog(true);
      }
      return;
    }
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <>
      <div className="flex items-center justify-between gap-3 rounded-lg border bg-card px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <Smartphone className="size-6 shrink-0 text-primary" />
          <p className="text-sm">
            {t(
              'Add the app to your home screen to get maintenance updates, rent reminders and messages from your landlord'
            )}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button size="sm" onClick={handleInstallClick}>
            {t('Install')}
          </Button>
          <Button size="icon" variant="ghost" onClick={dismiss}>
            <X className="size-4" />
          </Button>
        </div>
      </div>

      <ResponsiveDialog
        open={showIosDialog}
        setOpen={closeIosDialog}
        renderHeader={() => t('Add to Home Screen')}
        renderContent={() => (
          <StepSlider
            doneLabel={t('Got it')}
            onDone={() => closeIosDialog(false)}
            steps={getIosSteps(t)}
          />
        )}
      />

      <ResponsiveDialog
        open={showAndroidDialog}
        setOpen={closeAndroidDialog}
        renderHeader={() => t('Add to Home Screen')}
        renderContent={() => (
          <StepSlider
            doneLabel={t('Got it')}
            onDone={() => closeAndroidDialog(false)}
            steps={getAndroidSteps(t)}
          />
        )}
      />
    </>
  );
}
